// Pure SYNOP parsing functions — no I/O, no AWS, returns plain objects

// parseSynopLines: MetService HTML-derived line format (AAXX header blocks)
// wmoStations: { [id]: { stationName, latitude, longitude } }
// windFn: parseWindStandard | parseWindMetService
// Returns: [{ id, name, lat, lon, source, observations: [{ t, dd, ff, tt, mslp }] }]
export function parseSynopLines(lines, wmoStations, source, now, windFn = parseWindStandard) {
  const stations = new Map();
  let iw = 4;
  let obsTimeMs = null;

  for (const line of lines) {
    if (line.startsWith('AAXX')) {
      const m = line.match(/^AAXX (\d{2})(\d{2})(\d)/);
      if (m) {
        iw = parseInt(m[3]);
        obsTimeMs = resolveTimestamp(parseInt(m[1]), parseInt(m[2]), now);
      }
      continue;
    }

    if (!obsTimeMs || !/^\d{5} /.test(line)) continue;

    const tokens = line.replace(/=.*$/, '').trim().split(/\s+/);
    const id = tokens[0];
    if (tokens[1] === 'NIL') continue;

    const [dd, ff] = windFn(tokens[2], iw);
    const { tt, mslp } = parseGroups(tokens, 3);

    if (!stations.has(id)) {
      const info = wmoStations[id];
      if (!info) continue;
      stations.set(id, { id, name: info.stationName, lat: info.latitude, lon: info.longitude, source, observations: [] });
    }
    stations.get(id).observations.push({ t: obsTimeMs, dd, ff, tt, mslp });
  }

  const result = [...stations.values()];
  result.forEach(s => s.observations.sort((a, b) => a.t - b.t));
  return result;
}

// parseSynopCsv: Ogimet CSV format
// CSV columns: WMO_IND,YEAR,MONTH,DAY,HOUR,MIN,REPORT
// REPORT: "AAXX DDHHiw IIiii iRiXhVV Nddff ..."
// windFn: parseWindStandard | parseWindMetService
// Returns: [{ id, name, lat, lon, source, observations: [{ t, dd, ff, tt, mslp }] }]
export function parseSynopCsv(csv, wmoStations, source, windFn = parseWindStandard) {
  const stations = new Map();

  for (const raw of csv.split('\n')) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;

    const parts = line.split(',');
    if (parts.length < 7) continue;

    const id = parts[0].trim();
    const t = Date.UTC(+parts[1], +parts[2] - 1, +parts[3], +parts[4], +parts[5]);
    const tokens = parts.slice(6).join(',').trim().split(/\s+/);

    // tokens: [AAXX, DDHHiw, IIiii, iRiXhVV, Nddff, ...]
    if (tokens[0] !== 'AAXX' || tokens.length < 5) continue;
    if (tokens[3] === 'NIL') continue;

    const iw = parseInt(tokens[1][4]);
    const [dd, ff] = windFn(tokens[4], iw);
    const { tt, mslp } = parseGroups(tokens, 5);

    if (!stations.has(id)) {
      const info = wmoStations[id];
      stations.set(id, {
        id,
        name: info?.stationName ?? id,
        lat: info?.latitude ?? null,
        lon: info?.longitude ?? null,
        source,
        observations: [],
      });
    }
    stations.get(id).observations.push({ t, dd, ff, tt, mslp });
  }

  const result = [...stations.values()];
  result.forEach(s => s.observations.sort((a, b) => a.t - b.t));
  return result;
}

// Decode temperature and MSLP groups from a token array starting at offset
export function parseGroups(tokens, offset) {
  let tt = null, mslp = null;
  for (let i = offset; i < tokens.length; i++) {
    const g = tokens[i].replace(/=+$/, '');
    if (g.length !== 5 || g.includes('/')) continue;
    if (g[0] === '1') tt = (g[1] === '1' ? -1 : 1) * parseInt(g.slice(2)) / 10;
    if (g[0] === '4') {
      const pppp = parseInt(g.slice(1));
      mslp = (pppp < 5000 ? 10000 + pppp : pppp) / 10;
    }
  }
  return { tt, mslp };
}

// Standard SYNOP wind group Nddff: iw 0,1 = m/s → knots; iw 3,4 = knots (no conversion)
export function parseWindStandard(group, iw) {
  if (!group || group.length < 5 || group.slice(1).includes('/')) return [null, null];
  const dd = parseInt(group.slice(1, 3)) * 10;
  let ff = parseInt(group.slice(3, 5));
  if (iw === 0 || iw === 1) ff = Math.round(ff * 1.944);
  return [dd, ff];
}

// MetService-specific: iw=4 is m/s (non-standard), iw=3 is km/h
export function parseWindMetService(group, iw) {
  if (!group || group.length < 5 || group.slice(1).includes('/')) return [null, null];
  const dd = parseInt(group.slice(1, 3)) * 10;
  let ff = parseInt(group.slice(3, 5));
  if (iw === 4) ff = Math.round(ff * 1.944);
  if (iw === 3) ff = Math.round(ff / 1.852);
  return [dd, ff];
}

// Resolve AAXX day/hour to Unix ms — rolls back one month if day appears to be in the future
export function resolveTimestamp(day, hour, now) {
  const ref = new Date(now);
  const utc = new Date(Date.UTC(ref.getUTCFullYear(), ref.getUTCMonth(), day, hour));
  if (utc.getTime() > now) utc.setUTCMonth(utc.getUTCMonth() - 1);
  return utc.getTime();
}

// Strip HTML tags and split into clean SYNOP lines
export function extractSynopLines(html) {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/AAXX/g, '\nAAXX')
    .split('\n')
    .map(l => l.trim())
    .filter(l => l.length > 0);
}
